import { Link, List, ListItem, Typography, Box, Container, createTheme, ThemeProvider } from '@mui/material';
import * as CommonLicenseProps from './CommonLicenseProps';

// Custom theme using createTheme
const theme = createTheme(
  {
    typography:
    {
      fontFamily: "Inter", // Change the default font-family
    }
  });

const LicenseAgreementCustomer = () =>
{
  return (
    <>
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg">
          <Box sx={CommonLicenseProps.pageProps}>
            {/* Page Header */}
            <Typography sx={CommonLicenseProps.pageTitleProps}>
              License Agreement (Customer)
            </Typography>

            {/* Introduction */}
            <Typography sx={CommonLicenseProps.normalParaProps}>
              This LICENSE AGREEMENT (the “<Typography sx={CommonLicenseProps.emphasizedText}>Agreement</Typography>”) is made by and between you (“<Typography sx={CommonLicenseProps.emphasizedText}>Customer,</Typography>” “<Typography sx={CommonLicenseProps.emphasizedText}>you,</Typography>” or “<Typography sx={CommonLicenseProps.emphasizedText}>your</Typography>”) and Saagas.AI Inc., a Delaware corporation
              (“<Typography sx={CommonLicenseProps.emphasizedText}>Saagas</Typography>”).
            </Typography>
            <Typography sx={CommonLicenseProps.normalParaProps}>
              THIS AGREEMENT TAKES EFFECT WHEN YOU CLICK THE “PROCEED TO PAY” BUTTON BELOW OR BY ACCESSING OR USING THE INFLUENCER MARKETPLACE (the
              “<Typography sx={CommonLicenseProps.emphasizedText}>Effective Date</Typography>). BY CLICKING ON THE “PROCEED TO PAY” BUTTON BELOW OR BY ACCESSING OR USING THE INFLUENCER MARKETPLACE YOU (A)
              ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND THIS AGREEMENT; (B) REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, POWER, AND AUTHORITY TO ENTER INTO
              THIS AGREEMENT AND, IF ENTERING INTO THIS AGREEMENT FOR AN ORGANIZATION, THAT YOU HAVE THE LEGAL AUTHORITY TO BIND THAT ORGANIZATION; AND (C) ACCEPT
              THIS AGREEMENT AND AGREE THAT YOU ARE LEGALLY BOUND BY ITS TERMS
            </Typography>
            <Typography sx={CommonLicenseProps.normalParaProps}>
              IF YOU DO NOT AGREE TO THESE TERMS, YOU MAY NOT ACCESS OR USE THE INFLUENCER MARKETPLACE.
            </Typography>

            {/* Recitals */}
            <Typography sx={CommonLicenseProps.sectionHeadingProps}>
              Recitals
            </Typography>
            <List sx={CommonLicenseProps.upperAlphaListProps}>
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.bulletParaProps}>
                  Saagas, through its {' '} <Link href="https://www.saagas.ai/" target="_blank" rel="noopener noreferrer">website </Link>{' '} operates an online marketplace (the “<Typography sx={CommonLicenseProps.emphasizedText}>Influencer Marketplace</Typography>”) which connects influencers and customers, enabling influencers to license their name, image, likeness, biographical information, voice, and other indicia of personal identity (collectively, “<Typography sx={CommonLicenseProps.emphasizedText}>NIL</Typography>”), to customers in exchange for a license fee.
                </Typography>
              </ListItem>
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.bulletParaProps}>
                  When an influencer submits media containing their NIL to Saagas, Saagas combines it with Saagas’s proprietary artificial intelligence and the
                  customer’s prompts and brand-specific media to generate new media (e.g., images and videos) containing the influencer’s NIL and customer’s
                  brand-specific intellectual property. A customer can buy a license to use this new media in exchange for a project license fee that is shared
                  by Saagas and the influencer.
                </Typography>
              </ListItem>
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.bulletParaProps}>
                  You wish to use Saagas’s artificial intelligence tools and Influencer Marketplace to purchase licenses to use influencers’ NIL in the new
                  media, subject to the terms and conditions of this Agreement.
                </Typography>
              </ListItem>
            </List>

            {/* Agreement */}
            <Typography sx={CommonLicenseProps.sectionHeadingProps}>
              Agreement
            </Typography>
            <List sx={CommonLicenseProps.decimalListProps}>
              {/* Section 1 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  License to Saagas
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    {/* Subsection 1.1 */}
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Grant of Rights to Saagas
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You hereby grant to Saagas a worldwide, non-exclusive, irrevocable, fully transferable (in accordance with Section 11.5) right and license
                      to do all of the following:
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          reproduce, display, modify, transmit, and distribute, in any and all formats and media and otherwise as needed to display on the
                          Website, all of your brand-specific media that you deliver to Saagas (“<Typography sx={CommonLicenseProps.emphasizedText}>IP Media</Typography>”);
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          use the IP Media and the content therein to create a brand profile of your source-indicating intellectual property (“<Typography sx={CommonLicenseProps.emphasizedText}>Marks</Typography>”),
                          potentially including your trade dress, tradenames, trademarks, and service marks among other intellectual property, using Saagas’
                          artificial intelligence technology (your “<Typography sx={CommonLicenseProps.emphasizedText}>Brand Profile</Typography>”);
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          use the IP Media and Brand Profile to create new media containing your Marks using Saagas’ artificial intelligence technology and
                          permit the editing of that media, all within the Website (such media, “<Typography sx={CommonLicenseProps.emphasizedText}>Generated Assets</Typography>” and each a “<Typography sx={CommonLicenseProps.emphasizedText}>Generated Asset</Typography>”);
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          subject to an Influencer’s (defined below) approval of a Generated Asset, permit you to download that approved Generated Asset
                          (each, an “<Typography sx={CommonLicenseProps.emphasizedText}>Approved Asset</Typography>” and in the plural, “<Typography sx={CommonLicenseProps.emphasizedText}>Approved Assets</Typography>”); and
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          use the IP Media, Marks, and Brand Profile in order to perform Saagas’ obligations or exercise any of Saagas’ rights under this
                          Agreement, without further consent from, or any payment or other compensation to, you.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    {/* Subsection 1.2 */}
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Express Grants of Rights
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You expressly understand and agree that the rights you grant to Saagas includes the right for Saagas to use all IP Media (including any and
                      all copyrights, Marks, and other rights that exist in the IP Media) for training, refining, and operating Saagas’s generative artificial
                      intelligence program to create personas, brand profiles, and generated assets, in each case without the necessity of any further grant of
                      rights by you. You also expressly understand and agree that to the extent that your Brand Profile, any Generated Asset, or any other
                      intellectual property created by Saagas would be considered a derivative work of the IP Media, you expressly grant Saagas the right to
                      generate derivative works of the IP Media in any and all formats and media.
                    </Typography>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    {/* Subsection 1.3 */}
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Waiver of Moral Rights as to Saagas
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You irrevocably waive, to the maximum extent permitted by applicable law, all rights of paternity, integrity, attribution, disclosure,
                      withdrawal, and any other rights that may be known as “moral rights” (collectively, “<Typography sx={CommonLicenseProps.emphasizedText}>Moral Rights</Typography>”) with respect to Saagas’ use of the IP
                      Media, Marks, and Brand Profile in accordance with this Agreement. To the extent this waiver is not permitted by applicable law, you hereby
                      agree not to enforce such Moral Rights against Saagas, its permitted successors, licensees, sublicensees, and assigns.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 2 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Contracting with Influencers
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Customer-Influencer Contracts
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You understand that you may offer through the Website to license from an influencer (each, an
                      “<Typography sx={CommonLicenseProps.emphasizedText}>Influencer</Typography>”) the right to create Generated Assets for an Influencer’s approval and to use Approved Assets for a particular license period
                      subject to certain use restrictions. You may make such an offer to an Influencer through the Website by proposing a project to an Influencer for
                      the Influencer to accept. If the Influencer accepts the project proposal through the Website, then you and the Influencer will have entered into
                      an agreement together without Saagas (each such agreement, a “<Typography sx={CommonLicenseProps.emphasizedText}>Customer-Influencer Contract</Typography>”). You hereby acknowledge and agree that each
                      Customer-Influencer Contract automatically incorporates the terms of Section 2, Section 4, Section 5, and Section 7.2 of this Agreement and the
                      terms of Section 2, Section 3, and Section 6.2 of Saagas’ analogous agreement with the Influencer to use the Influencer Marketplace (the
                      “<Typography sx={CommonLicenseProps.emphasizedText}>Saagas-Influencer Contract</Typography>” ), available <Link href="/license-agreement-influencer" target="_blank">here</Link>. By entering into a Customer-Influencer Contract, you agree that: (i) you thereby owe the same
                      obligations set forth in Section 4 of this Agreement to Customer that you do to Saagas, (ii) the Influencer may enforce Saagas’ rights in
                      Section 5 against you, and (iii) you thereby make all of the same representations and warranties you made in Section 7.2 of this Agreement, but
                      to the Influencer rather than Saagas.
                    </Typography>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      License from Influencer
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      By entering into a Customer-Influencer Contract, you:
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          receive from the Influencer the right to generate new Generated Assets containing the Influencer’s NIL using Saagas’ artificial intelligence
                          technology and permit the editing of that media, all within the Website;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          subject to the Influencer’s approval of a Generated Asset, receive from the Influencer the right to download each Approved Asset and use
                          Approved Assets pursuant to the terms agreed to between you and Influencer in the Customer-Influencer Contract, which terms include the license
                          duration and the license scope; and
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          receive from the Influencer the right to use the Influencer’s NIL in conjunction with the Approved Assets to identify the subject of the
                          Approved Assets.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      License Limitations
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      The following terms apply to the license that the Influencer grants to you pursuant to a Customer-Influencer Contract unless you and the
                      Influencer expressly agree otherwise in that Customer-Influencer Contract:
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you may use the Approved Assets only for advertising or endorsement purposes as agreed between you and the Influencer and in no other manner;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you may not use an Approved Asset in a way which associates the Approved Asset or the Influencer with any of the following: tobacco, mature
                          language, nudity, politics, weapons, gambling, violence, or any other subject matter that you Influencer agree in the Customer-Influencer
                          Contract is not permitted;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          the license terminates upon the earliest to occur of:
                        </Typography>
                        <List sx={CommonLicenseProps.lowerRomanListProps}>
                          <ListItem sx={CommonLicenseProps.listItemProps}>
                            <Typography sx={CommonLicenseProps.bulletParaProps}>
                              Saagas’ delivery of notice to you that you have breached this Agreement;
                            </Typography>
                          </ListItem>
                          <ListItem sx={CommonLicenseProps.listItemProps}>
                            <Typography sx={CommonLicenseProps.bulletParaProps}>
                              Influencer’s delivery of notice to you that you have breached your Customer-Influencer Contract with that Influencer;
                            </Typography>
                          </ListItem>
                          <ListItem sx={CommonLicenseProps.listItemProps}>
                            <Typography sx={CommonLicenseProps.bulletParaProps}>
                              the license expiration date set forth in the Customer-Influencer Contract; and
                            </Typography>
                          </ListItem>
                        </List>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you will cease use of the Approved Assets on date the license terminates.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Compliance with the Law
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You agree to comply with all laws related to this Agreement or to any Customer-Influencer Contract, including the Federal Trade Commission&apos;s
                      Guides Concerning Endorsements and Testimonials. If you make any audio or visual communications regarding the Influencer and yourself or your
                      brands, products, or services, then you will clearly and conspicuously disclose your &quot;material connection&quot; with the Influencer, making
                      it clear that the Influencer is a paid influencer. You must place such disclosure in plain sight in close proximity to any audio or visual
                      communications that you make regarding Influencer and yourself or your brands, products, or services and the disclosure must be unavoidable. You
                      may not bury the disclosure in a link or place the disclosure in a string of hashtags or other disclosures. Such disclosure is regardless of any
                      space limitations of the platform, where you can use hashtags for the disclosure (like #ad or #sponsored).
                    </Typography>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Equitable Relief
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You acknowledge that your breach of a Customer-Influencer Contract may cause an Influencer irreparable damages, for which an award of damages
                      would not be adequate compensation, and agrees that, in the event of such breach or threatened breach, such Influencer will be entitled to seek
                      equitable relief, including a restraining order, orders for a preliminary or permanent injunction, specific performance, and any other relief that
                      may be available from any court, in addition to any other remedy to which the Influencer may be entitled at law or in equity. Such remedies are
                      not exclusive but are in addition to all other remedies available at law or in equity, subject to any express exclusions or limitations in the
                      Customer-Influencer Contract to the contrary.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 3 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Use of the Influencer Marketplace
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.bulletParaProps}>
                      You may not use, take screenshots of, or otherwise capture, NIL media that influencers deliver to Saagas which are displayed on the Influencer
                      Marketplace
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.bulletParaProps}>
                      You agree not to take screenshots of, or otherwise capture for your use, Generated Assets, except that you may download or otherwise capture
                      Approved Assets as you are expressly permitted pursuant to a valid Customer-Influencer Contract.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.bulletParaProps}>
                      You may edit Generated Assets using media editing tools Saagas offers within the Website, and you may not edit Generated Assets via any other
                      means
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.bulletParaProps}>
                      You may only make an agreement with an Influencer concerning Generated Assets within the Website pursuant to a valid Customer-Influencer
                      Contract.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 4 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Maintenance of Attribution Notices
                </Typography>
                <Typography sx={CommonLicenseProps.normalParaProps}>
                  You will not alter any attribution information that Saagas embeds within or otherwise associates with the Approved Assets (“<Typography sx={CommonLicenseProps.emphasizedText}>Attribution
                  Information</Typography>”). You will ensure that the Approved Assets are at all times remain marked with all Attribution Information in the manner provided by
                  Saagas (including attribution with respect to Influencer). You will not generate any version of a Generated Asset or Approved Asset which is
                  missing any Attribution Information. You will abide by the specifications and guidance provided by the Coalition for Content Provenance and
                  Authenticity (C2PA) concerning Approved Assets and this Agreement. You will not use any notices that conflict with, confuse, or negate the
                  Attribution Information notices Saagas provides.
                </Typography>
              </ListItem>

              {/* Section 5 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Payment
                </Typography>
                <Typography sx={CommonLicenseProps.normalParaProps}>
                  As consideration in full for the services provided hereunder, you will timely pay to Saagas all fees due to each Influencer under
                  Customer-Influencer Contract and all fees due to Saagas. You understand and agree that ten percent (10%) of the project license fee you agreed to
                  pay an Influencer pursuant to a Customer-Influencer Contract is owed to Saagas and that Saagas will retain that portion of the project license fee
                  and send the rest to the Influencer. You will enter into any agreement required by any third-party payment processor used by Saagas (“<Typography sx={CommonLicenseProps.emphasizedText}>Payment
                  Processor</Typography>”), and your breach of any such agreement will be deemed a material breach of this Agreement. You will keep a valid payment method on
                  file with a Payment Processor at all times during which you owe or will owe fees to Saagas, an Influencer, or to any third party related to this
                  Agreement. Time is of the essence with respect to all dates and time periods pertaining to payment which are set forth or referred to in this
                  Agreement.
                </Typography>
              </ListItem>

              {/* Section 6 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Confidentiality
                </Typography>
                <Typography sx={CommonLicenseProps.normalParaProps}>
                  In connection with this Agreement, you may access information that is treated as confidential by Saagas, including information about Saagas’s
                  business affairs, confidential intellectual property, trade secrets, business operations and strategies, marketing, creative elements, artwork,
                  visual representations, research material and data, specifications, processes, and technological developments, whether orally or in written,
                  electronic, or other form or media, and whether or not marked, designated, or otherwise identified as “confidential” (collectively, “<Typography sx={CommonLicenseProps.emphasizedText}>Confidential
                  Information</Typography>”). Confidential Information does not include information that, at the time of disclosure and as established by documentary evidence:
                  (a) is or becomes generally available to and known by the public other than as a result of, directly or indirectly, any breach of this Section by
                  you; (b) is or becomes available to you on a non-confidential basis from a third-party source, provided that such third party is not and was not
                  prohibited from disclosing such Confidential Information; (c) was known by or in the possession of you before being disclosed by or on behalf of
                  Saagas; or (d) was or is independently developed by you without reference to or use, in whole or in part, of any of Confidential Information. You
                  will: (i) protect and safeguard the confidentiality of Confidential Information with at least the same degree of care as you would protect your
                  own Confidential Information, but in no event with less than a commercially reasonable degree of care; (ii) not use Confidential Information, or
                  permit it to be accessed or used, for any purpose other than as set forth in this Agreement; and (iii) not disclose any such Confidential
                  Information to any person or entity, except: (A) to your employees, agents, consultants, and legal advisors who need to know the Confidential
                  Information to assist you, or act on your behalf, to exercise its rights or perform your obligations under the Agreement; or (B) pursuant to
                  applicable federal, state, or local law or regulation, or a valid order issued by a court or governmental agency of competent jurisdiction,
                  provided that you will first provide Saagas with prompt written notice of such requirement so that Saagas may seek, at its sole cost and expense,
                  a protective order or other remedy.
                </Typography>
              </ListItem>

              {/* Section 7 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Representations and Warranties
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Mutual Representations and Warranties. Each party represents and warrants to the other party that:
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          it has the full right, power, and authority to enter into this Agreement and to perform its obligations hereunder; and
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          when executed and delivered by such party, this Agreement will constitute the legal, valid, and binding obligation of such party,
                          enforceable against such party in accordance with its terms.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Your Representations and Warranties. You represent and warrant that:
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you have read, understand, and at all times will adhere to Saagas’s Terms of Use, available <Link href="/terms-of-use" target="_blank">here</Link>, and which are incorporated by reference here;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          each instance of IP Media and all intellectual property contained therein and rights related thereto either belong to or are appropriately
                          licensed to you;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          neither the IP Media itself nor the use of IP Media pursuant to the terms of this Agreement and a Customer-Influencer Contract will
                          constitute an infringement, misappropriation, or violation of any intellectual or proprietary right of any person or violate any applicable
                          regulation or law;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you have the full right, power, and authority to grant the rights and licenses granted: (i) hereunder and (ii) to any Influencer pursuant to
                          a Customer-Influencer Contract;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you have not granted and will not grant, any liens, security interests, or other encumbrances in, to, or under the IP Media or the Marks,
                          and no consents from or payments to any third party are or will be required by your grant and license of rights hereunder or by the
                          grantee’s exercise of such rights;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          the exercise by the grantees of the rights and license granted under this Agreement will not infringe or otherwise conflict with the rights
                          of any other person or entity;
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          there is no settled, pending, or threatened litigation, opposition, or other claim or proceeding challenging the validity, enforceability,
                          ownership, registration, or use of the IP Media or the Marks; and
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          you have not brought or threatened any claim against any third party alleging infringement of the IP Media or the Marks, nor is any third
                          party infringing or threatening to infringe the IP Media or the Marks.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>

                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Disclaimer of Representations and Warranties
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      EXCEPT AS EXPRESSLY PROVIDED IN SECTION 7.1, SAAGAS HEREBY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, WITH
                      RESPECT TO THE SERVICES PROVIDED HEREUNDER, INCLUDING SPECIFICALLY ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                      TITLE, NON-INFRINGEMENT, AND WARRANTIES THAT MAY ARISE OUT OF COURSE OF DEALING, COURSE OF PERFORMANCE, USAGE, OR TRADE PRACTICE.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 8 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Indemnification
                </Typography>
                <Typography sx={CommonLicenseProps.normalParaProps}>
                  You will indemnify, hold harmless, and, at Saagas’ option, defend Saagas and its affiliates, officers, directors, employees, agents, sublicensees,
                  successors, and assigns (each, a “<Typography sx={CommonLicenseProps.emphasizedText}>Indemnified Party</Typography>” and together the “<Typography sx={CommonLicenseProps.emphasizedText}>Indemnified Parties</Typography>”) from and against all losses, damages, liabilities,
                  deficiencies, claims, actions, judgments, settlements, interest, awards, penalties, fines, fees, costs, or expenses of whatever kind, including
                  reasonable attorneys’ fees, the cost of enforcing any right to indemnification hereunder, and the cost of pursuing any insurance providers
                  (collectively, “<Typography sx={CommonLicenseProps.emphasizedText}>Losses</Typography>”) incurred by Saagas relating to any claim, suit, action, or proceeding (“<Typography sx={CommonLicenseProps.emphasizedText}>Claim</Typography>”) arising out of or in connection with your
                  actual or alleged: (a) breach of this Agreement; (b) improper use of the Website; (c) interaction with any Influencer, including without
                  limitation any injuries, losses or damages (whether compensatory, direct, incidental, consequential or otherwise) of any kind arising in
                  connection with or as a result of such interaction; (d) failure to accurately report, collect or remit taxes; or (e) breach of any laws,
                  regulations or third party rights such as intellectual property or privacy rights. To the maximum extent permitted by applicable law, you agree to
                  indemnify, defend, and hold the Indemnified Parties harmless from and against any of the aforementioned Claims, regardless of Saagas’
                  responsibility for negligence and howsoever the same may be caused. You may not settle any Claim against Saagas unless Saagas consents to such
                  settlement, and further provided that Saagas will have the right, at its option, to defend itself against any such Claim or to participate in the
                  defense thereof by counsel of its own choice.
                </Typography>
              </ListItem>

              {/* Section 9 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Terms and Termination
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Terms
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      The term of this Agreement commences as of the Effective Date and will remain in force unless and until terminated as provided herein (the
                      “<Typography sx={CommonLicenseProps.emphasizedText}>Term</Typography>”).
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Termination by Saagas
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      Saagas may terminate this Agreement at any time without cause by providing written notice to you. Saagas may delete your account and profile
                      on the Website at any time without cause by providing written notice to you.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Termination for Cause
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      Either party may terminate this Agreement on written notice to the other party if the other party materially breaches this Agreement and fails
                      to cure such breach within 20 days after receiving written notice thereof.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Surviving Rights
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      Any rights or obligations of the parties in this Agreement which, by their nature, should survive termination or expiration of this Agreement
                      will survive any such termination or expiration, including the rights and obligations set forth in this Section 9 and Section 1.2, Section
                      1.3, Section 2.1, Section 2.4, Section 4, Section 5, Section 6, Section 7, Section 8, Section 10, and Section 11.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 10 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  Remedies
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      No Equitable Relief
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You acknowledge and agree that: (a) monetary damages at law are a fully adequate remedy to compensate you for any breach or threatened breach
                      of this Agreement by Saagas; and (b) an action at law for monetary damages is your sole and exclusive remedy for any such breach. No breach by
                      Saagas of this Agreement will entitle you to equitable relief, including specific performance, injunctive relief, rescission, or any other
                      form of equitable remedy.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Limitation of Liability
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NEITHER PARTY WILL BE LIABLE TO THE OTHER PARTY UNDER OR IN CONNECTION WITH THIS AGREEMENT
                      FOR ANY CONSEQUENTIAL, INCIDENTAL, INDIRECT, EXEMPLARY, SPECIAL, PUNITIVE, OR ENHANCED DAMAGES, OR FOR ANY LOSS OF ACTUAL OR ANTICIPATED
                      PROFITS (REGARDLESS OF HOW THESE ARE CLASSIFIED AS DAMAGES), WHETHER ARISING OUT OF BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), OR
                      OTHERWISE (INCLUDING THE ENTRY INTO, PERFORMANCE, OR BREACH OF THIS AGREEMENT), REGARDLESS OF WHETHER SUCH DAMAGE WAS FORESEEABLE AND WHETHER
                      EITHER PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. THE FOREGOING LIMITATIONS DO NOT APPLY TO: (A) YOUR INDEMNIFICATION
                      OBLIGATIONS UNDER SECTION 8; OR (B) LOSSES ARISING OUT OF OR RELATING TO YOUR FAILURE TO COMPLY WITH THE CONFIDENTIALITY OBLIGATIONS UNDER
                      SECTION 6.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Obligation of Saagas
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You acknowledge and agree that Saagas has no obligation whatsoever to enforce Saagas’ agreement with any Influencer. You further acknowledge
                      and agree that Saagas is not a party to any Customer-Influencer Contract and therefore has no right to enforce any Customer-Influencer
                      Contract.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>

              {/* Section 11 */}
              <ListItem sx={CommonLicenseProps.listItemProps}>
                <Typography sx={CommonLicenseProps.subSectionHeadingProps}>
                  General
                </Typography>
                <List sx={CommonLicenseProps.decimalListProps}>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Interpretation
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      For purposes of this Agreement: (a) the words “include,” “includes,” and “including” are deemed to be followed by the words “without
                      limitation”; (b) the word “or” is not exclusive; and (c) the words “herein,” “hereof,” “hereby,” “hereto,” and “hereunder” refer to this
                      Agreement as a whole. This Agreement is intended to be construed without regard to any presumption or rule requiring construction or
                      interpretation against the party drafting an instrument or causing any instrument to be drafted.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Binding Agreement
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      This Agreement will be binding upon and will inure to the benefit of the successors and assigns of the respective parties hereto.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Entire Agreement
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      This Agreement, together with any exhibits or other attachments (which are hereby expressly incorporated into this Agreement), is the sole and
                      entire agreement of the parties with respect to the subject matter herein, and supersedes all prior and contemporaneous understandings,
                      agreements, representations, and warranties, whether written or oral, regarding such subject matter.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Severability
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      If any term or provision of this Agreement is invalid, illegal, or unenforceable in any jurisdiction, such invalidity, illegality, or
                      unenforceability will not affect the enforceability of any other term or provision of this Agreement or invalidate or render unenforceable
                      such term or provision in any other jurisdiction.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Assignment
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      You will not assign any of its rights or delegate any of your obligations under this Agreement without the prior written consent of Saagas.
                      Any purported assignment or delegation in violation of this Section is null and void. Saagas may freely assign or otherwise transfer any of
                      its rights or delegate any of its obligations under this Agreement. This Agreement is binding upon and inures to the benefit of the parties
                      hereto and their respective permitted successors and assigns.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Governing Law
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      This Agreement and all matters arising out of or relating to this Agreement are governed by, and construed in accordance with, the laws of the
                      State of Washington, without giving effect to any conflict of laws provisions thereof that would require or permit the application of the laws
                      of a different jurisdiction.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Arbitration
                    </Typography>
                    <List sx={CommonLicenseProps.lowerAlphaListProps}>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          Except as otherwise provided in Section 11.7(d), any dispute, controversy, or claim arising out of the subject matter of this Agreement
                          will be settled by arbitration before a single arbitrator in Seattle, Washington.
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          The arbitration will be initiated by filing a claim with American Arbitration Association and will be conducted by an arbitrator
                          knowledgeable in business law in accordance with the then-current rules of American Arbitration Association.
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          The resolution of any dispute, controversy, or claim as determined by the arbitrator will be binding on the parties. Judgment on the award
                          of the arbitrator may be entered by any party in any court having jurisdiction.
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          A party may seek from a court an order to compel arbitration, or any other interim relief or provisional remedies pending an arbitrator’s
                          resolution of any dispute, controversy, or claim. Any such action, suit, or proceeding will be litigated in courts located in Seattle,
                          Washington.
                        </Typography>
                      </ListItem>
                      <ListItem sx={CommonLicenseProps.listItemProps}>
                        <Typography sx={CommonLicenseProps.bulletParaProps}>
                          For the purposes set forth in Section 11.7(d), each party consents and submits to the jurisdiction of any local, state, or federal court
                          located in Seattle, Washington.
                        </Typography>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Contra Proferentem
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      The doctrine of contra proferentem will not apply to this Agreement. Any rule of law or any legal decision that would require interpretation
                      of any claimed ambiguities in this Agreement against the drafting party has no application and is expressly waived.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Setoff
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      Saagas may offset the amount of any claim Saagas has against you under this Agreement against any amount Saagas or its payment processor would
                      otherwise owe to you. On giving notice of a claim, Saagas will have the right to withhold payment of that portion of the amount outstanding
                      from Saagas to you that equals the amount of the estimated claim, and such withholding will not constitute a default under this Agreement. For
                      the avoidance of any doubt, Saagas’ indemnification rights under this Agreement are not limited to the possible amount of setoff.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Independence of Customer
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      Your relationship with Saagas is that of an independent individual and not an employee, agent, joint venturer, or partner of Saagas, except
                      that Saagas acts as a payment collection agent as described herein. You understand and agree that you have complete discretion over whether
                      you will enter into an agreement with an Influencer through the Website as well as: (i) what price you will pay for a license to use the
                      Approved Assets, (ii) the duration of a license you purchase to use the Approved Assets, and (iii) any additional restrictions you choose to
                      accept on a license to use the Approved Assets.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      No Third-Party Beneficiaries
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      This Agreement is for the sole benefit of the parties hereto and their respective successors and permitted assigns, and nothing herein,
                      express or implied, is intended to or will confer upon any third party any legal or equitable right, benefit, or remedy of any nature
                      whatsoever, under or by reason of this Agreement.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Amendment and Modification
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      No amendment or modification to this Agreement is effective unless it is in writing and signed by an authorized representative of each party.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Waiver
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      No waiver by any party of any of the provisions hereof will be effective unless explicitly set forth in writing and signed by the waiving
                      party. No waiver by any party will operate or be construed as a waiver of any failure, breach, or default not expressly identified by such
                      written waiver, whether of a similar or different character, and whether occurring before or after that waiver. Except as otherwise set forth
                      in this Agreement, no failure to exercise, or delay in exercising, any right, remedy, power, or privilege arising from this Agreement will
                      operate or be construed as a waiver thereof. No single or partial exercise of any right, remedy, power, or privilege hereunder will preclude
                      any other or further exercise thereof or the exercise of any other right, remedy, power, or privilege.
                    </Typography>
                  </ListItem>
                  <ListItem sx={CommonLicenseProps.listItemProps}>
                    <Typography sx={CommonLicenseProps.subSubSectionHeadingProps}>
                      Attorneys’ Fees
                    </Typography>
                    <Typography sx={CommonLicenseProps.normalParaProps}>
                      In the event that any claim, suit, action, arbitration, or proceeding is instituted or commenced by either party hereto against the other
                      party arising out of or related to this Agreement, the prevailing party will be entitled to recover its reasonable attorneys’ fees and court
                      costs from the non-prevailing party.
                    </Typography>
                  </ListItem>
                </List>
              </ListItem>
            </List>
          </Box>
        </Container>
      </ThemeProvider>
    </>
  );
};

export default LicenseAgreementCustomer;
